/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Avatar,
  Dialog,
  DialogContent,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import {
  FormElement,
  FormDialogTitle,
  FormDialogActions,
  FormCancelButton,
  getFieldProps,
  getSubmitButtonProps,
  OutlinedIcons,
} from '@marageti/z4-lib';

const { OutlinedDocumentIcon } = OutlinedIcons;

const schema = z.object({
  title: z.string().min(1, 'Title is required'),
});

type FormValues = z.infer<typeof schema>;

type TicketFileUploadModalProps = {
  open: boolean;
  file: File | null;
  onClose: () => void;
  onSubmit: (title: string) => Promise<void>;
};

const TicketFileUploadModal = ({
  open,
  file,
  onClose,
  onSubmit,
}: TicketFileUploadModalProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { title: '' },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFormSubmit = async (data: FormValues) => {
    await onSubmit(data.title);
    reset();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <FormDialogTitle handleClose={handleClose} sx={{ pb: 2.5 }}>Upload File</FormDialogTitle>
      <FormElement name="ticket-file-upload-form" onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent sx={{ pt: 0 }}>
          <Controller
            control={control}
            name="title"
            render={({ field }) => (
              <TextField
                {...field}
                label="Title"
                fullWidth
                required
                sx={{ mb: 2 }}
                {...getFieldProps('title', errors)}
              />
            )}
          />

          {file && (
            <ListItem disableGutters>
              <ListItemAvatar>
                <Avatar variant="rounded">
                  <OutlinedDocumentIcon sx={{ width: 24, height: 24 }} />
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary={file.name} />
            </ListItem>
          )}
        </DialogContent>
        <FormDialogActions>
          <FormCancelButton onClick={handleClose} />
          <LoadingButton
            type="submit"
            variant="contained"
            {...getSubmitButtonProps(isSubmitting, isValid)}
          >
            Submit
          </LoadingButton>
        </FormDialogActions>
      </FormElement>
    </Dialog>
  );
};

export default TicketFileUploadModal;
